import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Download, Filter, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

interface AssessmentRecord {
  project_name: string;
  date_scheduled: string;
  date_completed: string | null;
  salesforce_opportunity_id: string | null;
  completion_percentage: number;
  annual_savings_usd: string | null;
  roi_percentage: string | null;
  is_complete: boolean;
  project_id: string;
}

type SortField = 'project_name' | 'date_scheduled' | 'date_completed' | 'annual_savings_usd' | 'roi_percentage';
type SortDirection = 'asc' | 'desc';

export default function AssessmentsReport() {
  const [assessments, setAssessments] = useState<AssessmentRecord[]>([]);
  const [filteredAssessments, setFilteredAssessments] = useState<AssessmentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showCompleted, setShowCompleted] = useState(true);
  const [showIncomplete, setShowIncomplete] = useState(true);
  
  // Sort states
  const [sortField, setSortField] = useState<SortField>('date_scheduled');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  const { toast } = useToast();

  useEffect(() => {
    fetchAssessments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [assessments, startDate, endDate, showCompleted, showIncomplete]);

  const fetchAssessments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await apiRequest('/api/admin/assessments-report');
      setAssessments(data);
    } catch (error) {
      console.error('Error fetching assessments:', error);
      setError('Failed to load assessments data');
      toast({
        title: "Error",
        description: "Failed to load assessments data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...assessments];
    
    // Date range filter
    if (startDate) {
      filtered = filtered.filter(assessment => 
        new Date(assessment.date_scheduled) >= new Date(startDate)
      );
    }
    
    if (endDate) {
      filtered = filtered.filter(assessment => 
        new Date(assessment.date_scheduled) <= new Date(endDate)
      );
    }
    
    // Completion status filter
    if (!showCompleted) {
      filtered = filtered.filter(assessment => !assessment.is_complete);
    }
    
    if (!showIncomplete) {
      filtered = filtered.filter(assessment => assessment.is_complete);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];
      
      // Handle null values
      if (aValue === null && bValue === null) return 0;
      if (aValue === null) return sortDirection === 'asc' ? 1 : -1;
      if (bValue === null) return sortDirection === 'asc' ? -1 : 1;
      
      // Convert to appropriate types for comparison
      if (sortField === 'date_scheduled' || sortField === 'date_completed') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (sortField === 'annual_savings_usd' || sortField === 'roi_percentage') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    setFilteredAssessments(filtered);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Project Name',
      'Date Scheduled', 
      'Date Completed',
      'Completion %',
      'Annual Savings (USD)',
      'ROI %',
      'Status',
      'Salesforce Opportunity ID'
    ];
    
    const csvContent = [
      headers.join(','),
      ...filteredAssessments.map(assessment => [
        `"${assessment.project_name}"`,
        `"${format(new Date(assessment.date_scheduled), 'yyyy-MM-dd')}"`,
        assessment.date_completed ? `"${format(new Date(assessment.date_completed), 'yyyy-MM-dd')}"` : '""',
        assessment.completion_percentage,
        assessment.annual_savings_usd ? parseFloat(assessment.annual_savings_usd).toLocaleString() : '0',
        assessment.roi_percentage ? parseFloat(assessment.roi_percentage).toFixed(1) : '0',
        assessment.is_complete ? 'Completed' : 'In Progress',
        assessment.salesforce_opportunity_id ? `"${assessment.salesforce_opportunity_id}"` : '""'
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `assessments-report-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Success",
      description: "Assessments report exported successfully",
    });
  };

  const formatCurrency = (value: string | null) => {
    if (!value || value === '0') return '$0';
    return `$${parseFloat(value).toLocaleString()}`;
  };

  const formatPercentage = (value: string | null) => {
    if (!value || value === '0') return '0%';
    return `${parseFloat(value).toFixed(1)}%`;
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-white">Assessments Report</h1>
        </div>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <div className="text-center text-gray-400">Loading assessments data...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-white">Assessments Report</h1>
        </div>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <div className="text-center text-red-400">{error}</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Assessments Report</h1>
          <p className="text-gray-400">View and export value assessment records</p>
        </div>
        <Button 
          onClick={exportToCSV}
          className="bg-[#4AE2C9] text-gray-900 hover:bg-[#4AE2C9]/90"
          disabled={filteredAssessments.length === 0}
        >
          <Download className="w-4 h-4 mr-2" />
          Export CSV ({filteredAssessments.length} records)
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="startDate" className="text-gray-300">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="bg-gray-900 border-gray-600 text-white"
              />
            </div>
            <div>
              <Label htmlFor="endDate" className="text-gray-300">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="bg-gray-900 border-gray-600 text-white"
              />
            </div>
            <div className="flex items-center gap-4 pt-6">
              <label className="flex items-center gap-2 text-gray-300">
                <input
                  type="checkbox"
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                  className="rounded border-gray-600"
                />
                Show Completed
              </label>
              <label className="flex items-center gap-2 text-gray-300">
                <input
                  type="checkbox"
                  checked={showIncomplete}
                  onChange={(e) => setShowIncomplete(e.target.checked)}
                  className="rounded border-gray-600"
                />
                Show In Progress
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-[#4AE2C9]">{filteredAssessments.length}</div>
            <div className="text-sm text-gray-400">Total Records</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-400">
              {filteredAssessments.filter(a => a.is_complete).length}
            </div>
            <div className="text-sm text-gray-400">Completed</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-yellow-400">
              {filteredAssessments.filter(a => !a.is_complete).length}
            </div>
            <div className="text-sm text-gray-400">In Progress</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-[#4AE2C9]">
              {formatCurrency(
                filteredAssessments
                  .filter(a => a.annual_savings_usd)
                  .reduce((sum, a) => sum + parseFloat(a.annual_savings_usd!), 0)
                  .toString()
              )}
            </div>
            <div className="text-sm text-gray-400">Total Savings</div>
          </CardContent>
        </Card>
      </div>

      {/* Data Table */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Assessment Records</CardTitle>
          <CardDescription className="text-gray-400">
            Click column headers to sort. Use filters above to narrow results.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-gray-300">
              <thead>
                <tr className="border-b border-gray-600">
                  <th 
                    className="text-left p-3 cursor-pointer hover:bg-gray-700 transition-colors"
                    onClick={() => handleSort('project_name')}
                  >
                    <div className="flex items-center gap-2">
                      Project Name
                      <SortIcon field="project_name" />
                    </div>
                  </th>
                  <th 
                    className="text-left p-3 cursor-pointer hover:bg-gray-700 transition-colors"
                    onClick={() => handleSort('date_scheduled')}
                  >
                    <div className="flex items-center gap-2">
                      Date Scheduled
                      <SortIcon field="date_scheduled" />
                    </div>
                  </th>
                  <th 
                    className="text-left p-3 cursor-pointer hover:bg-gray-700 transition-colors"
                    onClick={() => handleSort('date_completed')}
                  >
                    <div className="flex items-center gap-2">
                      Date Completed
                      <SortIcon field="date_completed" />
                    </div>
                  </th>
                  <th className="text-left p-3">Status</th>
                  <th 
                    className="text-left p-3 cursor-pointer hover:bg-gray-700 transition-colors"
                    onClick={() => handleSort('annual_savings_usd')}
                  >
                    <div className="flex items-center gap-2">
                      Annual Savings
                      <SortIcon field="annual_savings_usd" />
                    </div>
                  </th>
                  <th 
                    className="text-left p-3 cursor-pointer hover:bg-gray-700 transition-colors"
                    onClick={() => handleSort('roi_percentage')}
                  >
                    <div className="flex items-center gap-2">
                      ROI %
                      <SortIcon field="roi_percentage" />
                    </div>
                  </th>
                  <th className="text-left p-3">Salesforce ID</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssessments.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center p-8 text-gray-500">
                      No assessments match the current filters.
                    </td>
                  </tr>
                ) : (
                  filteredAssessments.map((assessment, index) => (
                    <tr key={index} className="border-b border-gray-700 hover:bg-gray-700/30">
                      <td className="p-3 font-medium text-white">
                        {assessment.project_name}
                      </td>
                      <td className="p-3">
                        {format(new Date(assessment.date_scheduled), 'MMM dd, yyyy')}
                      </td>
                      <td className="p-3">
                        {assessment.date_completed 
                          ? format(new Date(assessment.date_completed), 'MMM dd, yyyy')
                          : '—'
                        }
                      </td>
                      <td className="p-3">
                        <Badge 
                          variant={assessment.is_complete ? "default" : "secondary"}
                          className={assessment.is_complete ? "bg-green-600" : "bg-yellow-600"}
                        >
                          {assessment.is_complete ? "Completed" : `${assessment.completion_percentage}%`}
                        </Badge>
                      </td>
                      <td className="p-3 text-[#4AE2C9] font-medium">
                        {formatCurrency(assessment.annual_savings_usd)}
                      </td>
                      <td className="p-3 text-green-400">
                        {formatPercentage(assessment.roi_percentage)}
                      </td>
                      <td className="p-3 text-xs text-gray-400">
                        {assessment.salesforce_opportunity_id || '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}